import * as Sentry from "@sentry/browser";
import { getWebSocketStreamDomain } from "../../constants";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useState,
} from "react";
import TokenStore from "services/TokenStore";
import { isEqual } from "lodash";

type WSHandler = <T>(channel: string, data: T) => void;

interface HandlerEntry {
  prefix: string | string[];
  handler: WSHandler;
}

type WebsocketEvent<T> = {
  channel: string;
  type: string;
  message?: string;
  data: T;
};

const DISCONNECT_DELAY_MS = 30000; // 30 seconds
const VISIBILITY_DISCONNECT_DELAY_MS = 15 * 60 * 1000; // 15 minutes
const MAX_RETRIES = 8;

const WebSocketContext = createContext({
  registerHandler: (_prefix: string | string[], _handler: WSHandler) => {},
  unregisterHandler: (_prefix: string | string[], _handler: WSHandler) => {},
  isConnected: false,
});

export const useWebSocketContext = () => {
  return useContext(WebSocketContext);
};

export default ({ children }: { children: ReactNode }) => {
  const handlers = useRef<HandlerEntry[]>([]);
  const ws = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const disconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const visibilityDisconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryDelayRef = useRef<number>(500); // Start with 500ms
  const retryCountRef = useRef<number>(0);
  const isConnectingRef = useRef<boolean>(false);
  const shouldConnectRef = useRef<boolean>(false);

  const connect = useCallback(() => {
    if (
      isConnectingRef.current ||
      (ws.current && ws.current.readyState === WebSocket.OPEN)
    ) {
      return;
    }

    const accessToken = TokenStore.getLocalAccessToken();
    if (!accessToken?.value) {
      console.debug("No access token available, skipping WebSocket connection");
      return;
    }

    isConnectingRef.current = true;

    // Clear any existing retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    console.debug("Attempting WebSocket connection...");
    const socket = new WebSocket(getWebSocketStreamDomain());
    ws.current = socket;

    socket.onopen = () => {
      console.debug("WebSocket connected");
      isConnectingRef.current = false;
      setIsConnected(true);

      socket.send(
        JSON.stringify({
          type: "authenticate",
          accessToken: accessToken.value,
        }),
      );
    };

    socket.onmessage = (event) => {
      // Reset retry state on successful message receipt
      retryDelayRef.current = 500;
      retryCountRef.current = 0;

      let payload: WebsocketEvent<any>;
      try {
        payload = JSON.parse(event.data);
      } catch {
        console.warn("Invalid JSON:", event.data);
        return;
      }
      console.debug("Received message:", payload);
      if (payload.type === "system") {
        console.debug("System message:", payload.message);
        return;
      } else if (payload.type === "push") {
        const channel = payload.channel;
        if (!channel) return;

        handlers.current.forEach(({ prefix, handler }) => {
          if (
            Array.isArray(prefix)
              ? prefix.some((p) => channel.startsWith(p))
              : channel.startsWith(prefix)
          ) {
            handler(channel, payload.data);
          }
        });
      }
    };

    socket.onclose = (event) => {
      const closeReason =
        event.code === 1006
          ? "Abnormal closure (network issue, server restart, etc.)"
          : event.reason || "Unknown reason";

      console.debug(`WebSocket closed ${event.code}: ${closeReason}`);
      isConnectingRef.current = false;
      setIsConnected(false);

      // Only retry if we should be connected (have handlers) and haven't exhausted retries
      if (
        shouldConnectRef.current &&
        handlers.current.length > 0 &&
        retryCountRef.current < MAX_RETRIES
      ) {
        const delay = retryDelayRef.current;
        retryCountRef.current += 1;
        console.debug(
          `Retrying connection in ${delay}ms ` +
            `(attempt ${retryCountRef.current}/${MAX_RETRIES})`,
        );

        retryTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);

        // Exponential backoff: double the delay, max 30 seconds
        retryDelayRef.current = Math.min(delay * 2, 30000);
      } else if (retryCountRef.current >= MAX_RETRIES) {
        console.debug("Max WebSocket retries reached, waiting for tab focus");
      }
    };

    socket.onerror = (event) => {
      // The error event itself doesn't contain much useful information
      // The real details come in the subsequent onclose event
      console.debug(
        "WebSocket error occurred - details will follow in close event",
      );
      isConnectingRef.current = false;
      Sentry.captureException(new Error("WebSocket error"), {
        extra: { event },
      });
    };
  }, []);

  const disconnect = useCallback(() => {
    shouldConnectRef.current = false;

    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
  }, []);

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === "hidden") {
      // Start disconnect timer when page becomes hidden
      if (visibilityDisconnectTimeoutRef.current) {
        clearTimeout(visibilityDisconnectTimeoutRef.current);
      }

      visibilityDisconnectTimeoutRef.current = setTimeout(() => {
        console.debug("Disconnecting WebSocket due to inactivity");
        disconnect();
      }, VISIBILITY_DISCONNECT_DELAY_MS);
    } else if (document.visibilityState === "visible") {
      // Clear disconnect timer and reconnect immediately when page becomes visible
      if (visibilityDisconnectTimeoutRef.current) {
        clearTimeout(visibilityDisconnectTimeoutRef.current);
        visibilityDisconnectTimeoutRef.current = null;
      }

      // Reconnect if we have handlers — reset retry state for a fresh attempt
      if (handlers.current.length > 0) {
        retryCountRef.current = 0;
        retryDelayRef.current = 500;
        shouldConnectRef.current = true;
        connect();
      }
    }
  }, [connect, disconnect]);

  useEffect(() => {
    // Add visibility change listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      // Cleanup on unmount
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
        disconnectTimeoutRef.current = null;
      }

      if (visibilityDisconnectTimeoutRef.current) {
        clearTimeout(visibilityDisconnectTimeoutRef.current);
        visibilityDisconnectTimeoutRef.current = null;
      }

      disconnect();
    };
  }, [disconnect, handleVisibilityChange]);

  const registerHandler = useCallback(
    (prefix: string | string[], handler: WSHandler) => {
      if (Array.isArray(prefix) ? prefix.length === 0 : !prefix) {
        throw new Error("Prefix is required");
      }

      // Clear any pending disconnect timeout
      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
        disconnectTimeoutRef.current = null;
      }

      handlers.current.push({ prefix, handler });

      // Lazy connect: only connect when first handler is registered
      if (handlers.current.length === 1) {
        shouldConnectRef.current = true;
        connect();
      }
    },
    [connect],
  );

  const unregisterHandler = useCallback(
    (prefix: string | string[], handler: WSHandler) => {
      handlers.current = handlers.current.filter(
        (h) =>
          !(Array.isArray(prefix)
            ? isEqual(h.prefix, prefix) && h.handler === handler
            : h.prefix === prefix && h.handler === handler),
      );

      // Disconnect after delay when no handlers remain
      if (handlers.current.length === 0) {
        disconnectTimeoutRef.current = setTimeout(() => {
          disconnect();
        }, DISCONNECT_DELAY_MS);
      }
    },
    [disconnect],
  );

  return (
    <WebSocketContext.Provider
      value={{ registerHandler, unregisterHandler, isConnected }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
